import { useMemo } from 'react'
import ReactQuill, { Quill } from 'react-quill'
import 'react-quill/dist/quill.snow.css'

interface RichTextEditorProps {
  value: string
  onChange: (html: string) => void
  placeholder?: string
  maxLength?: number
}

/**
 * Register a customized Link blot once so that all inserted/pasted anchors
 * open in a new tab safely (target="_blank" rel="noopener noreferrer").
 * Quill's default link sanitizer still runs (blocks javascript:, allows
 * http/https/mailto/tel), since we extend the built-in Link format.
 *
 * Guarded with a module-level flag so HMR/re-renders don't trigger
 * duplicate-registration warnings.
 */
let linkBlotRegistered = false

const registerSafeLinkBlot = (): void => {
  if (linkBlotRegistered) return
  linkBlotRegistered = true

  // The built-in Link format is a Parchment Blot class with a static create().
  // We only need to override the static create(), so type it as a constructible
  // class exposing that static. `object` keeps it a valid base for `extends`.
  interface LinkBlot {
    new (...args: unknown[]): object
    create(value: string): HTMLAnchorElement
  }

  const Link = Quill.import('formats/link') as unknown as LinkBlot

  class SafeLink extends Link {
    static create(value: string): HTMLAnchorElement {
      const node = super.create(value)
      node.setAttribute('target', '_blank')
      node.setAttribute('rel', 'noopener noreferrer')
      return node
    }
  }

  Quill.register(SafeLink, true)
}

registerSafeLinkBlot()

const stripHtml = (html: string) => html.replace(/<[^>]+>/g, '').trim()

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Enter your message',
  maxLength,
}) => {
  const modules = useMemo(
    () => ({
      toolbar: [
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ color: [] }, { background: [] }],
        ['link'],
        ['clean'],
      ],
    }),
    []
  )

  // Restrict to simple formats; exclude font, size, image, video, etc.
  const formats = [
    'bold',
    'italic',
    'underline',
    'strike',
    'list',
    'bullet',
    'color',
    'background',
    'link',
  ]

  const handleChange = (html: string) => {
    if (maxLength) {
      const textLength = stripHtml(html).length
      if (textLength > maxLength) return
    }
    onChange(html)
  }

  const textCount = stripHtml(value || '').length

  return (
    <div className="flex flex-col gap-1">
      <ReactQuill
        theme="snow"
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        modules={modules}
        formats={formats}
      />
      {maxLength && (
        <div className="text-xs text-black/45 text-right">
          {textCount}/{maxLength}
        </div>
      )}
    </div>
  )
}

export default RichTextEditor
